/**
 * GraphQL Hooks for Critiques
 *
 * Apollo Client hooks for critique operations with automatic
 * transformation to REST-compatible types for component compatibility.
 *
 * Feature: 063-graphql-frontend
 * Tasks: T022-T026, T029
 */

import { useQuery } from '@apollo/client';
import {
  GET_CRITIQUES_FOR_PHOTO,
  GET_CRITIQUES_FOR_COLLECTION,
  GET_CRITIQUE_EDITS,
  GET_CRITIQUE_SESSIONS,
  GET_CRITIQUE_SESSION,
  GET_RECENT_CRITIQUE_SESSIONS,
  GET_SESSION_CONTRIBUTORS,
} from '../queries';
import type {
  GraphQLCritique,
  GraphQLCritiqueEdit,
  GraphQLCritiqueSession,
  PaginatedResult,
} from '../types';
import type { Critique } from '../../types/critique';
import type { CritiqueEdit, CritiqueEditType, EditData } from '../../types/edit.types';
import { getVariantUrl } from '../../components/photos/AdaptivePhoto';
import type { ImageVariant } from '../../types/photo';
import { parseCritiqueContent } from '../../utils/critiqueContentParser';
import logger from '../../utils/logger';

/**
 * Helper to map GraphQL variants to ImageVariant format
 * Handles both camelCase (GraphQL) and snake_case (raw DB) field names
 */
function mapVariants(variants: any[] | undefined): ImageVariant[] {
  if (!variants) return [];
  return variants.map((v: any) => ({
    id: v.id || '',
    variantType: (v.variantType || v.variantKey || v.variant_type || 'medium') as ImageVariant['variantType'],
    storageUrl: v.storageUrl || v.storageKey || v.storage_key || '',
    width: v.width || 0,
    height: v.height || 0,
    fileSize: v.fileSize || v.file_size || 0,
    format: v.format || 'jpeg',
  }));
}

interface GetCritiquesForPhotoData {
  critiquesForPhoto: PaginatedResult<GraphQLCritique>;
}

interface GetCritiquesForCollectionData {
  critiquesForCollection: PaginatedResult<GraphQLCritique>;
}

interface GetCritiqueEditsData {
  critiqueEdits: PaginatedResult<GraphQLCritiqueEdit>;
}

interface GetCritiqueSessionsData {
  critiqueSessions: PaginatedResult<GraphQLCritiqueSession>;
}

interface GetCritiqueSessionData {
  critiqueSession: GraphQLCritiqueSession;
}

/**
 * Transform GraphQL Critique to REST Critique
 * Feature 069 Phase 7: Parse edits from markdown blocks instead of using edits array
 * Feature 081: Include photo version for attachment rendering
 */
function mapGraphQLCritiqueToREST(gqlCritique: GraphQLCritique): Critique {
  // Feature 069 Phase 7: Parse edits from content (API synthesizes blocks from legacy edits)
  const { edit, markup, cleanedContent, errors } = parseCritiqueContent(gqlCritique.content);

  if (errors.length > 0) {
    logger.warn('CritiquesGraphQL', 'Errors parsing critique content', {
      critiqueId: gqlCritique.id,
      errors,
    });
  }

  // Get editId from the original edit if present
  const editId = edit?.id || gqlCritique.editId || null;

  // Feature 081: Map photo version for attachment rendering
  const photoVersion = gqlCritique.photoVersion ? {
    id: gqlCritique.photoVersion.id,
    globalId: gqlCritique.photoVersion.globalId,
    photoId: gqlCritique.photoVersion.photoId,
    versionNumber: gqlCritique.photoVersion.versionNumber,
    isCurrent: gqlCritique.photoVersion.isCurrent,
    width: gqlCritique.photoVersion.width,
    height: gqlCritique.photoVersion.height,
    thumbnailUrl: gqlCritique.photoVersion.thumbnailUrl,
    previewUrl: gqlCritique.photoVersion.previewUrl,
    fullUrl: gqlCritique.photoVersion.fullUrl,
    createdAt: gqlCritique.photoVersion.createdAt,
  } : null;

  return {
    id: gqlCritique.id,
    globalId: gqlCritique.globalId,
    content: cleanedContent, // Display content without edit/markup blocks (they're rendered as previews)
    photoId: gqlCritique.photoId,
    collectionId: null, // GraphQL doesn't expose this yet
    parentId: gqlCritique.parentId || null,
    editId,
    edit,
    markup,
    threadDepth: 0, // Computed client-side
    createdAt: gqlCritique.createdAt,
    userId: gqlCritique.userId,
    username: gqlCritique.user?.username || '',
    displayName: gqlCritique.user?.displayName || '',
    // Feature 081: Photo version for attachment rendering
    photoVersionId: gqlCritique.photoVersionId || null,
    photoVersion,
  };
}

/**
 * Transform GraphQL CritiqueEdit to REST CritiqueEdit
 */
function mapGraphQLCritiqueEditToREST(gqlEdit: GraphQLCritiqueEdit): CritiqueEdit {
  return {
    id: gqlEdit.id,
    critiqueId: gqlEdit.critiqueId,
    baseEditId: gqlEdit.parentEditId || null,
    editType: gqlEdit.editType as CritiqueEditType,
    editData: gqlEdit.edits as unknown as EditData,
    createdAt: gqlEdit.createdAt,
    updatedAt: gqlEdit.updatedAt,
  };
}

/**
 * Query: Get critiques for a specific photo
 */
export function useCritiqueDetailGraphQL(photoId: string | undefined, limit: number = 50) {
  const { data, loading, error, refetch } = useQuery<GetCritiquesForPhotoData>(
    GET_CRITIQUES_FOR_PHOTO,
    {
      // CRITICAL: Always provide placeholder values when skipping to prevent Apollo Client
      // from validating required variables before checking skip.
      variables: { photoId: photoId || 'skip-placeholder', limit, offset: 0 },
      skip: !photoId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const critiques: Critique[] = data?.critiquesForPhoto?.items
    ? data.critiquesForPhoto.items.map(mapGraphQLCritiqueToREST)
    : [];

  return {
    data: critiques,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

/**
 * Query: Get critiques for a collection
 */
export function useCollectionCritiquesGraphQL(collectionId: string | undefined, limit: number = 100) {
  const { data, loading, error, refetch } = useQuery<GetCritiquesForCollectionData>(
    GET_CRITIQUES_FOR_COLLECTION,
    {
      // CRITICAL: Always provide placeholder values when skipping to prevent Apollo Client
      // from validating required variables before checking skip.
      variables: { collectionId: collectionId || 'skip-placeholder', limit, offset: 0 },
      skip: !collectionId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const critiques: Critique[] = data?.critiquesForCollection?.items
    ? data.critiquesForCollection.items.map(mapGraphQLCritiqueToREST)
    : [];

  return {
    data: critiques,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

/**
 * Query: Get critiques for a specific photo within a collection context
 *
 * This is the GraphQL equivalent of useCollectionCritiques (REST).
 * It fetches all critiques for the collection/session and filters client-side by photoId.
 *
 * Note: We fetch up to 1000 critiques and filter client-side because the GraphQL API
 * doesn't currently support filtering by photoId. For typical collection sizes (under
 * 100 photos with ~10 critiques each), this is acceptable. For larger collections,
 * consider adding server-side photoId filtering to the critiquesForCollection query.
 *
 * @param collectionId - ID of the collection
 * @param photoId - ID of the photo (nullable to support deselection)
 * @param limit - Number of critiques to return after filtering (default 30)
 * @param sessionId - Session ID to filter critiques by session
 * @param pollInterval - Optional polling interval in ms (default: 0 = no polling).
 *                       Set to e.g. 3000 to poll every 3 seconds for AI feedback updates.
 */
export function useCollectionPhotoCritiquesGraphQL(
  collectionId: string,
  photoId: string | null,
  limit: number = 30,
  sessionId: string | undefined,
  pollInterval: number = 0
) {
  const shouldSkip = !collectionId || !photoId || !sessionId;
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<GetCritiquesForCollectionData>(
    GET_CRITIQUES_FOR_COLLECTION,
    {
      // CRITICAL: Always provide placeholder values when skipping to prevent Apollo Client
      // from validating required variables before checking skip.
      variables: {
        collectionId: collectionId || 'skip-placeholder',
        sessionId: sessionId || 'skip-placeholder',
        limit: 1000,
        offset: 0,
      },
      skip: shouldSkip,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      pollInterval,
    }
  );

  // Filter critiques for the specific photo
  const allItems = data?.critiquesForCollection?.items || [];
  const filteredItems = allItems.filter(c => c.photoId === photoId);

  const critiques: Critique[] = filteredItems
    .slice(0, limit)
    .map(mapGraphQLCritiqueToREST);

  return {
    data: { critiques, pagination: { limit, offset: 0, total: critiques.length } },
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
    startPolling,
    stopPolling,
  };
}

/**
 * Query: Get edits for a critique
 */
export function useCritiqueEditsGraphQL(critiqueId: string | undefined) {
  const { data, loading, error, refetch } = useQuery<GetCritiqueEditsData>(
    GET_CRITIQUE_EDITS,
    {
      // CRITICAL: Always provide placeholder values when skipping to prevent Apollo Client
      // from validating required variables before checking skip.
      variables: { critiqueId: critiqueId || 'skip-placeholder', limit: 50, offset: 0 },
      skip: !critiqueId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const edits: CritiqueEdit[] = data?.critiqueEdits?.items
    ? data.critiqueEdits.items.map(mapGraphQLCritiqueEditToREST)
    : [];

  return {
    data: edits,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

/**
 * Helper to transform a GraphQL session to component format
 */
function mapSessionToComponent(session: GraphQLCritiqueSession) {
  // Map critiques to recentComments format (up to 5)
  const recentComments = (session.critiques?.items || []).slice(0, 5).map((critique: any) => {
    const variants = mapVariants(critique.photo?.variants);
    // Parse content to extract markup for shape references
    const { markup } = parseCritiqueContent(critique.content);
    return {
      globalId: critique.globalId,
      username: critique.user?.username || '',
      text: critique.content,
      createdAt: critique.createdAt,
      photo: critique.photo ? {
        id: critique.photo.id,
        thumbnailUrl: getVariantUrl({ id: critique.photo.id, variants }, 'thumbnail') || '',
        description: null,
        variants,
      } : {
        id: critique.photoId,
        thumbnailUrl: '',
        description: null,
        variants: [],
      },
      markup,
    };
  });

  return {
    id: session.globalId,
    globalId: session.globalId,
    title: session.title,
    slug: session.slug,
    state: session.isActive ? 'open' as const : 'closed' as const,
    // Feature 071: Include AI session indicator for badge display
    isAiSession: session.isAiSession || false,
    createdAt: session.createdAt,
    collection: session.collection ? {
      globalId: session.collection.globalId,
      title: session.collection.name || session.collection.title,
      slug: session.collection.slug,
      heroPhoto: session.collection.heroPhoto ? {
        id: session.collection.heroPhoto.id,
        description: session.collection.heroPhoto.description || null,
        variants: mapVariants(session.collection.heroPhoto.variants),
      } : null,
      owner: session.collection.user ? {
        id: session.collection.user.id,
        username: session.collection.user.username,
        displayName: session.collection.user.displayName || session.collection.user.username,
      } : undefined,
    } : {
      globalId: '',
      title: '',
      slug: '',
      heroPhoto: null,
    },
    recentComments,
  };
}

/**
 * Query: List critique sessions with infinite scroll support
 *
 * Returns sessions with loadMore function for infinite scroll.
 * @param limit - Number of sessions per page (default: 20)
 *
 * Note: Kept at 20 to avoid CDN rate limiting when loading hero images.
 * Infinite scroll will load more as user scrolls.
 */
export function useCritiqueSessionsGraphQL(limit: number = 20) {
  const { data, loading, error, refetch, fetchMore } = useQuery<GetCritiqueSessionsData>(
    GET_CRITIQUE_SESSIONS,
    {
      variables: { limit, offset: 0 },
      fetchPolicy: 'cache-and-network',
    }
  );

  /**
   * Load more critique sessions using cursor-based pagination.
   * Automatically merges new results with existing data.
   * Handles errors gracefully with logging.
   */
  const loadMore = async () => {
    if (!data?.critiqueSessions?.totalCount) {
      return;
    }

    const currentOffset = data.critiqueSessions.items?.length ?? 0;
    const hasMore = currentOffset < data.critiqueSessions.totalCount;

    if (!hasMore) {
      return;
    }

    try {
      await fetchMore({
        variables: {
          limit,
          offset: currentOffset,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return {
            critiqueSessions: {
              ...fetchMoreResult.critiqueSessions,
              items: [
                ...(prev.critiqueSessions?.items || []),
                ...(fetchMoreResult.critiqueSessions?.items || []),
              ],
            },
          };
        },
      });
    } catch (err) {
      logger.error('useCritiquesGraphQL', 'Failed to load more critique sessions', err);
    }
  };

  const sessions = data?.critiqueSessions?.items?.map(mapSessionToComponent) || [];
  const totalCount = data?.critiqueSessions?.totalCount ?? 0;
  const hasMore = sessions.length < totalCount;

  return {
    sessions,
    totalCount,
    hasMore,
    loading,
    error,
    refetch,
    loadMore,
  };
}

/**
 * Query: Get single critique session by ID
 *
 * @param sessionId - Session global ID
 * @param pollInterval - Optional polling interval in ms (default: 0 = no polling).
 *                       Set to e.g. 3000 to poll every 3 seconds for AI feedback updates.
 */
export function useCritiqueSessionGraphQL(sessionId: string | undefined, pollInterval: number = 0) {
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<GetCritiqueSessionData>(
    GET_CRITIQUE_SESSION,
    {
      // CRITICAL: Always provide a placeholder ID when skipping to prevent Apollo Client
      // from validating required variables before checking skip.
      variables: { id: sessionId || 'skip-placeholder' },
      skip: !sessionId,
      fetchPolicy: 'cache-and-network',
      pollInterval,
    }
  );

  const session = data?.critiqueSession ? {
    id: data.critiqueSession.id,
    globalId: data.critiqueSession.globalId,
    title: data.critiqueSession.title,
    slug: data.critiqueSession.slug,
    description: data.critiqueSession.description || null,
    state: data.critiqueSession.isActive ? 'open' : 'closed',
    collectionId: data.critiqueSession.collectionId,
    createdAt: data.critiqueSession.createdAt,
    updatedAt: data.critiqueSession.updatedAt,
    // Feature 071: AI session fields
    intent: data.critiqueSession.intent || 'open',
    isAiSession: data.critiqueSession.isAiSession || false,
    // Feature 071 Phase 10: Paused state for AI sessions
    isPaused: data.critiqueSession.isPaused || false,
    aiFeedbackStatus: data.critiqueSession.aiFeedbackStatus || null,
    // Feature 649: Feedback options - transform to include computed convenience flags
    feedbackOptions: data.critiqueSession.feedbackOptions ? {
      enabled: data.critiqueSession.feedbackOptions.enabled,
      hasGeneral: data.critiqueSession.feedbackOptions.enabled.includes('general'),
      hasRating: data.critiqueSession.feedbackOptions.enabled.includes('rating'),
    } : null,
    // Feature 651: N Comments task fields
    nCommentsEnabled: data.critiqueSession.nCommentsEnabled || false,
    nCommentsTargetCount: data.critiqueSession.nCommentsTargetCount || null,
    nCommentsDefaultCount: data.critiqueSession.nCommentsDefaultCount || null,
    collectionPhotoCount: data.critiqueSession.collectionPhotoCount || 0,
    collection: data.critiqueSession.collection ? {
      id: data.critiqueSession.collection.id,
      globalId: data.critiqueSession.collection.globalId,
      name: data.critiqueSession.collection.name || data.critiqueSession.collection.title,
      slug: data.critiqueSession.collection.slug,
      photoCount: data.critiqueSession.collection.photoCount ?? 0,
      heroPhotoId: data.critiqueSession.collection.heroPhotoId,
      heroPhoto: data.critiqueSession.collection.heroPhoto,
    } : undefined,
    critiques: data.critiqueSession.critiques?.items?.map(mapGraphQLCritiqueToREST) || [],
    critiqueCount: data.critiqueSession.critiques?.totalCount ?? 0,
  } : undefined;

  return {
    data: session,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
    startPolling,
    stopPolling,
  };
}

/**
 * Map of photoId to array of critiques
 */
export interface PhotoCritiquesMap {
  [photoId: string]: Critique[];
}

/**
 * Query: Get top-level critiques for multiple photos in a collection
 * This fetches all critiques for the collection in a single query,
 * then groups them by photoId and filters for top-level only.
 *
 * @param collectionId - ID of the collection
 * @param photoIds - Array of photo IDs to get critiques for
 * @param limit - Number of critiques per photo (default 5)
 * @param sessionId - Session ID to filter critiques by session
 * @param pollInterval - Optional polling interval in ms (default: 0 = no polling).
 *                       Set to e.g. 3000 to poll every 3 seconds for AI feedback updates.
 */
export function useCollectionPhotosCritiquesGraphQL(
  collectionId: string | undefined,
  photoIds: string[],
  limit: number = 5,
  sessionId: string | undefined,
  pollInterval: number = 0
) {
  const shouldSkip = !collectionId || !sessionId || photoIds.length === 0;
  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery<GetCritiquesForCollectionData>(
    GET_CRITIQUES_FOR_COLLECTION,
    {
      // CRITICAL: Always provide placeholder values when skipping to prevent Apollo Client
      // from validating required variables before checking skip.
      variables: {
        collectionId: collectionId || 'skip-placeholder',
        sessionId: sessionId || 'skip-placeholder',
        limit: 1000, // Fetch all critiques for collection, we'll filter client-side
        offset: 0,
      },
      skip: shouldSkip,
      fetchPolicy: 'cache-and-network',
      pollInterval,
    }
  );

  // Build map of photoId -> top-level critiques (limited to `limit` per photo)
  const critiquesMap: PhotoCritiquesMap = {};

  if (data?.critiquesForCollection?.items) {
    // Initialize empty arrays for all requested photoIds
    photoIds.forEach(photoId => {
      critiquesMap[photoId] = [];
    });

    // Group critiques by photoId, filtering for top-level only (parentId === null)
    data.critiquesForCollection.items.forEach(gqlCritique => {
      const photoId = gqlCritique.photoId;
      if (photoId && photoIds.includes(photoId) && !gqlCritique.parentId) {
        // Only add if we haven't reached the limit for this photo
        if (critiquesMap[photoId].length < limit) {
          critiquesMap[photoId].push(mapGraphQLCritiqueToREST(gqlCritique));
        }
      }
    });
  }

  return {
    critiquesMap,
    isLoading: loading,
    isError: !!error,
    errors: error ? [error] : [],
    refetch,
    startPolling,
    stopPolling,
  };
}

interface GetRecentCritiqueSessionsData {
  recentCritiqueSessions: PaginatedResult<GraphQLCritiqueSession>;
}

/**
 * Query: List recently viewed critique sessions
 * Feature: 067-recent-critiques
 */
export function useRecentCritiqueSessionsGraphQL(page: number = 1, limit: number = 50) {
  const offset = (page - 1) * limit;

  const { data, loading, error, refetch } = useQuery<GetRecentCritiqueSessionsData>(
    GET_RECENT_CRITIQUE_SESSIONS,
    {
      variables: { limit, offset },
      fetchPolicy: 'cache-and-network',
    }
  );

  const sessions = data?.recentCritiqueSessions?.items?.map(session => {
    // Map critiques to recentComments format (up to 5)
    const recentComments = (session.critiques?.items || []).slice(0, 5).map((critique: any) => {
      const variants = mapVariants(critique.photo?.variants);
      // Parse content to extract markup for shape references
      const { markup } = parseCritiqueContent(critique.content);
      return {
        globalId: critique.globalId,
        username: critique.user?.username || '',
        text: critique.content,
        createdAt: critique.createdAt,
        photo: critique.photo ? {
          id: critique.photo.id,
          thumbnailUrl: getVariantUrl({ id: critique.photo.id, variants }, 'thumbnail') || '',
          description: null,
          variants,
        } : {
          id: critique.photoId,
          thumbnailUrl: '',
          description: null,
          variants: [],
        },
        markup,
      };
    });

    return {
      id: session.globalId,
      globalId: session.globalId,
      title: session.title,
      slug: session.slug,
      state: session.isActive ? 'open' as const : 'closed' as const,
      // Feature 071: Include AI session indicator for badge display
      isAiSession: session.isAiSession || false,
      createdAt: session.createdAt,
      collection: session.collection ? {
        globalId: session.collection.globalId,
        title: session.collection.name || session.collection.title,
        slug: session.collection.slug,
        heroPhoto: session.collection.heroPhoto ? {
          id: session.collection.heroPhoto.id,
          description: session.collection.heroPhoto.description || null,
          variants: mapVariants(session.collection.heroPhoto.variants),
        } : null,
        owner: session.collection.user ? {
          id: session.collection.user.id,
          username: session.collection.user.username,
          displayName: session.collection.user.displayName || session.collection.user.username,
        } : undefined,
      } : {
        globalId: '',
        title: '',
        slug: '',
        heroPhoto: null,
      },
      recentComments,
    };
  }) || [];

  return {
    data: {
      sessions,
      pagination: {
        page,
        limit,
        total: data?.recentCritiqueSessions?.totalCount ?? 0,
        totalPages: Math.ceil((data?.recentCritiqueSessions?.totalCount ?? 0) / limit),
      },
    },
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

/**
 * Contributor user for display in tooltip
 */
export interface ContributorUser {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
}

/**
 * Critique contributors categorized by role
 */
export interface CritiqueContributors {
  photographer: ContributorUser;
  critics: ContributorUser[];
  lurkers: ContributorUser[];
  totalCount: number;
}

interface GetSessionContributorsData {
  sessionContributors: CritiqueContributors | null;
}

/**
 * Query: Get contributors to a critique session
 */
export function useSessionContributorsGraphQL(sessionId: string | undefined) {
  const { data, loading, error, refetch } = useQuery<GetSessionContributorsData>(
    GET_SESSION_CONTRIBUTORS,
    {
      // CRITICAL: Always provide a placeholder ID when skipping to prevent Apollo Client
      // from validating required variables before checking skip.
      variables: { sessionId: sessionId || 'skip-placeholder' },
      skip: !sessionId,
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    data: data?.sessionContributors || null,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}
